import { create } from 'zustand';

export interface SelectedOrgPlanningScenario {
  readonly scenarioId: string;
  readonly headChangeSetId: string;
}

interface OrgPlanningScenarioState {
  readonly selectedByDiagram: Readonly<Record<string, SelectedOrgPlanningScenario | undefined>>;
  readonly modeByDiagram: Readonly<Record<string, 'live' | 'scenario' | undefined>>;
  readonly editEpochByDiagram: Readonly<Record<string, number | undefined>>;
  readonly editingContextsByDiagram: Readonly<Record<string, Readonly<Record<string, string>>>>;
  beginEditing: (diagramId: string, fallbackMode: 'live' | 'scenario') => number;
  adopt: (diagramId: string, scenario: SelectedOrgPlanningScenario, expectedEpoch: number) => boolean;
  adoptEmpty: (diagramId: string, expectedEpoch: number) => boolean;
  bindEditingContext: (diagramId: string, scenarioId: string, context: string) => void;
  setMode: (diagramId: string, mode: 'live' | 'scenario') => void;
  select: (diagramId: string, scenario: SelectedOrgPlanningScenario | null, activate?: boolean) => void;
  clearAll: () => void;
}

/**
 * Tab-local memory of the selected server plan per diagram. The store is
 * in-memory, so after a reload with two or more open plans the rediscovery
 * effect could not choose one and every plan-backed surface rendered empty —
 * the saved draft "disappeared" (QA issue 78). Only the scenario id is
 * remembered; the head change-set id must always come from the fresh server
 * heads list.
 */
const SELECTED_PLAN_STORAGE_PREFIX = 'creately-org-plan-selection:';

export function recallSelectedOrgPlanScenarioId(diagramId: string): string | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    return sessionStorage.getItem(`${SELECTED_PLAN_STORAGE_PREFIX}${diagramId}`);
  } catch {
    return null;
  }
}

function rememberSelectedOrgPlanScenarioId(diagramId: string, scenarioId: string | null): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (scenarioId) sessionStorage.setItem(`${SELECTED_PLAN_STORAGE_PREFIX}${diagramId}`, scenarioId);
    else sessionStorage.removeItem(`${SELECTED_PLAN_STORAGE_PREFIX}${diagramId}`);
  } catch {
    // Storage unavailable — restore is best-effort.
  }
}

/** Diagram-local scenario selection shared by compatible Atlas projections. */
export const useOrgPlanningScenarioStore = create<OrgPlanningScenarioState>((set, get) => ({
  selectedByDiagram: {},
  modeByDiagram: {},
  editEpochByDiagram: {},
  editingContextsByDiagram: {},
  bindEditingContext: (diagramId, scenarioId, context) => set((state) => ({
    editingContextsByDiagram: { ...state.editingContextsByDiagram,
      [diagramId]: { ...state.editingContextsByDiagram[diagramId], [scenarioId]: context } },
  })),
  beginEditing: (diagramId, fallbackMode) => {
    const state = get();
    const fromLive = (state.modeByDiagram[diagramId] ?? fallbackMode) === 'live';
    const epoch = (state.editEpochByDiagram[diagramId] ?? 0) + (fromLive ? 1 : 0);
    const selectedByDiagram = { ...state.selectedByDiagram };
    // Leaving Live starts one new editing session, not one session per field.
    // Retained named plans remain discoverable; only active UI selection clears.
    if (fromLive) delete selectedByDiagram[diagramId];
    set({ selectedByDiagram,
      modeByDiagram: { ...state.modeByDiagram, [diagramId]: 'scenario' },
      editEpochByDiagram: { ...state.editEpochByDiagram, [diagramId]: epoch },
    });
    return epoch;
  },
  adopt: (diagramId, scenario, expectedEpoch) => {
    const state = get();
    if ((state.editEpochByDiagram[diagramId] ?? 0) !== expectedEpoch) return false;
    rememberSelectedOrgPlanScenarioId(diagramId, scenario.scenarioId);
    set({ selectedByDiagram: { ...state.selectedByDiagram, [diagramId]: scenario },
      modeByDiagram: { ...state.modeByDiagram, [diagramId]: 'scenario' },
      editingContextsByDiagram: { ...state.editingContextsByDiagram,
        [diagramId]: { ...state.editingContextsByDiagram[diagramId],
          [scenario.scenarioId]: orgPlanningEditingContextKey(diagramId, state) } },
    });
    return true;
  },
  adoptEmpty: (diagramId, expectedEpoch) => {
    const state = get();
    if ((state.editEpochByDiagram[diagramId] ?? 0) !== expectedEpoch) return false;
    const selectedByDiagram = { ...state.selectedByDiagram };
    delete selectedByDiagram[diagramId];
    rememberSelectedOrgPlanScenarioId(diagramId, null);
    set({ selectedByDiagram });
    return true;
  },
  setMode: (diagramId, mode) => set((state) => ({
    modeByDiagram: { ...state.modeByDiagram, [diagramId]: mode },
    editEpochByDiagram: { ...state.editEpochByDiagram,
      [diagramId]: (state.editEpochByDiagram[diagramId] ?? 0)
        + (state.modeByDiagram[diagramId] !== mode ? 1 : 0) },
  })),
  select: (diagramId, scenario, activate = true) => {
    rememberSelectedOrgPlanScenarioId(diagramId, scenario?.scenarioId ?? null);
    set((state) => {
      const selectedByDiagram = { ...state.selectedByDiagram };
      if (scenario) selectedByDiagram[diagramId] = scenario;
      else delete selectedByDiagram[diagramId];
      return {
        selectedByDiagram,
        editEpochByDiagram: { ...state.editEpochByDiagram,
          [diagramId]: (state.editEpochByDiagram[diagramId] ?? 0)
            + (state.selectedByDiagram[diagramId]?.scenarioId !== scenario?.scenarioId
              || state.selectedByDiagram[diagramId]?.headChangeSetId !== scenario?.headChangeSetId ? 1 : 0) },
        ...(scenario && activate ? { modeByDiagram: { ...state.modeByDiagram, [diagramId]: 'scenario' as const } } : {}),
      };
    });
  },
  clearAll: () => set({ selectedByDiagram: {}, modeByDiagram: {}, editEpochByDiagram: {}, editingContextsByDiagram: {} }),
}));

/** Stable while own saves advance a head, distinct when another plan is selected. */
export function orgPlanningEditingContextKey(
  diagramId: string,
  state: OrgPlanningScenarioState = useOrgPlanningScenarioStore.getState(),
): string {
  const scenarioId = state.selectedByDiagram[diagramId]?.scenarioId;
  return scenarioId
    ? state.editingContextsByDiagram[diagramId]?.[scenarioId] ?? `plan:${scenarioId}`
    : `editing:${state.editEpochByDiagram[diagramId] ?? 0}`;
}
